// v2.14.0 — Rendu des ui_components issus du function calling backend.
// Types supportés : place_list, map, transport_buttons, route_summary, address_preview.
// Pas de bulle agent autour (rendu pleine largeur sous le texte, cohérent avec address_navigate_card).
// v2.15.1 — Ajout : bouton "Ouvrir le navigateur" prominent + bouton ⭐ favori (chat).
// v2.17.2a — Renommé "Ouvrir Mode Livreur" → "Ouvrir le navigateur".

import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import MiniMapPreview from "@/components/MiniMapPreview";
import OrdonnanceCard from "@/components/OrdonnanceCard";
import { CniCard, FactureCard, AnalyseMedicaleCard, CountryNote } from "@/components/MagicCards";
import useUserId from "@/hooks/useUserId";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const TIER_COLORS = {
  partner: "#A855F7",
  premium: "#F59E0B",
  basic: "#3B82F6",
  free: "#64748B",
};

function PlaceList({ items, query }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid="agent-place-list" style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
      {items.map((p, i) => (
        <div key={`${p.adresse_civ || p.nom || i}-${i}`} data-testid={`agent-place-item-${i}`} style={{
          padding: "8px 10px",
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 8,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          fontSize: 13,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <strong style={{ color: "#F1F5F9" }}>{p.nom}</strong>
            <span style={{ display: "flex", gap: 5, alignItems: "center" }}>
              {p.is_mock && (
                <span data-testid={`agent-place-test-badge-${i}`} style={{
                  fontSize: 9.5, padding: "1px 8px", borderRadius: 999, fontWeight: 900, letterSpacing: 1,
                  background: "#F59E0B", color: "#1A1206",
                }}>TEST</span>
              )}
              {p.sponsored_tier && p.sponsored_tier !== "free" && (
                <span style={{
                  fontSize: 10, padding: "1px 6px", borderRadius: 999, fontWeight: 700,
                  background: TIER_COLORS[p.sponsored_tier] + "33",
                  color: TIER_COLORS[p.sponsored_tier],
                  border: `1px solid ${TIER_COLORS[p.sponsored_tier]}55`,
                }}>{p.sponsored_tier.toUpperCase()}</span>
              )}
            </span>
          </div>
          {p.adresse_civ && <code style={{ fontSize: 11, color: "#93C5FD" }}>{p.adresse_civ}</code>}
          {p.sub_category && <span style={{ fontSize: 11, color: "#94A3B8" }}>{p.sub_category}</span>}
          {p.telephone && <a href={`tel:${p.telephone}`} style={{ fontSize: 11, color: "#34D399", textDecoration: "none" }}>📞 {p.telephone}</a>}
        </div>
      ))}
      {query && <small style={{ fontSize: 10, color: "#64748B", textAlign: "right" }}>requête : {query}</small>}
    </div>
  );
}

function MapBlock({ component, userId }) {
  const hasCoords = Number.isFinite(component.lat) && Number.isFinite(component.lng);
  const label = component.label || component.commune || component.city || "Destination";
  const addressCiv = component.address_civ || component.code || null;
  const [savingFav, setSavingFav] = useState(false);
  const [favSaved, setFavSaved] = useState(false);
  const [favName, setFavName] = useState(label);
  const [showFavPopup, setShowFavPopup] = useState(false);

  const openLivreur = () => {
    if (!hasCoords) return;
    const params = new URLSearchParams({ dest: `${component.lat},${component.lng}`, label });
    if (addressCiv) params.set("code", addressCiv);
    window.location.href = `/navigate?${params.toString()}`;
  };

  const saveFavorite = async () => {
    if (!hasCoords || !addressCiv) { toast.error("Coordonnées manquantes"); return; }
    if (!favName.trim()) { toast.error("Donne un nom à ton favori"); return; }
    setSavingFav(true);
    try {
      const r = await axios.post(`${API}/user/favorites`, {
        user_id: userId,
        name: favName.trim(),
        address_civ: addressCiv,
        lat: component.lat,
        lng: component.lng,
        source: "chat",
      });
      if (r.data?.ok) {
        toast.success(`⭐ ${favName.trim()} ajouté à tes favoris`);
        setFavSaved(true);
        setShowFavPopup(false);
      } else {
        toast.error(r.data?.reason || "Erreur favori");
      }
    } catch (e) {
      toast.error(e.response?.data?.reason || e.message);
    } finally {
      setSavingFav(false);
    }
  };

  return (
    <div data-testid="agent-map-block" style={{ marginTop: 10, borderRadius: 8, overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)" }}>
      <MiniMapPreview lat={component.lat} lng={component.lng} onSet={() => { /* read-only ici */ }} height={200} />
      <div style={{ padding: "8px 10px", background: "#0F172A", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12 }}>
        <strong style={{ color: "#F1F5F9" }}>📍 {label}</strong>
        {addressCiv && <code style={{ color: "#93C5FD", fontSize: 11 }}>{addressCiv}</code>}
      </div>
      {hasCoords && (
        <div style={{ display: "flex", gap: 6, padding: "8px 10px", background: "#0F172A", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <button
            type="button"
            data-testid="open-livreur-btn"
            onClick={openLivreur}
            style={{
              flex: 1,
              padding: "10px 14px",
              background: "linear-gradient(135deg, #F59E0B, #FBBF24)",
              color: "#0F172A",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 700,
              boxShadow: "0 2px 8px rgba(251,191,36,0.4)",
            }}
          >↗️ Ouvrir le navigateur</button>
          {addressCiv && (
            <button
              type="button"
              data-testid="save-favorite-btn"
              onClick={() => setShowFavPopup((v) => !v)}
              disabled={favSaved}
              style={{
                padding: "10px 14px",
                background: favSaved ? "rgba(34,197,94,0.2)" : "rgba(255,255,255,0.08)",
                color: favSaved ? "#86EFAC" : "#F1F5F9",
                border: `1px solid ${favSaved ? "#22C55E" : "rgba(255,255,255,0.15)"}`,
                borderRadius: 8,
                cursor: favSaved ? "default" : "pointer",
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >{favSaved ? "⭐ Enregistré" : "⭐ Favori"}</button>
          )}
        </div>
      )}
      {showFavPopup && hasCoords && addressCiv && (
        <div data-testid="favorite-popup" style={{ padding: "10px 12px", background: "rgba(15,23,42,0.95)", borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 11, color: "#94A3B8" }}>Nom du favori</label>
          <input
            type="text"
            data-testid="favorite-name-input"
            value={favName}
            onChange={(e) => setFavName(e.target.value)}
            placeholder="Ex: Sococé"
            style={{ padding: 6, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 4, fontSize: 12 }}
          />
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" onClick={() => setShowFavPopup(false)} style={{ padding: "6px 10px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#CBD5E1", borderRadius: 4, cursor: "pointer", fontSize: 11 }}>Annuler</button>
            <button type="button" data-testid="favorite-confirm-btn" disabled={savingFav} onClick={saveFavorite} style={{ padding: "6px 12px", background: "#F59E0B", color: "#0F172A", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{savingFav ? "..." : "⭐ Sauver"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function TransportButtons({ component }) {
  const opts = (component.options || []).filter((o) => !o.hidden);
  if (opts.length === 0) return null;
  const origin = component.origin;

  const onClick = (opt) => {
    // v2.17.2 — Routing : private_car → /navigate (internal). yango → deeplink universel + fallback web.
    //            sotra → ouvre la page d'info Sotra. walking → ne fait rien (info only).
    if (opt.provider === 'private_car' && opt.deeplink) {
      window.location.href = opt.deeplink;
      return;
    }
    if (opt.provider === 'yango' && opt.deeplink) {
      // Tentative deeplink natif (yango://...) avec fallback web après 1.2s si pas ouvert.
      const fallback = opt.fallback_url;
      const start = Date.now();
      window.location.href = opt.deeplink;
      if (fallback) {
        setTimeout(() => {
          if (Date.now() - start < 2000 && document.hasFocus()) {
            window.open(fallback, '_blank', 'noopener');
          }
        }, 1200);
      }
      return;
    }
    if (opt.provider === 'sotra' && opt.info_url) {
      window.open(opt.info_url, '_blank', 'noopener');
      return;
    }
    if (opt.deeplink) {
      if (opt.kind === 'internal') window.location.href = opt.deeplink;
      else window.open(opt.deeplink, '_blank', 'noopener');
    }
  };

  const cardBg = {
    yango: 'linear-gradient(135deg, rgba(245,158,11,0.18), rgba(245,158,11,0.06))',
    sotra: 'linear-gradient(135deg, rgba(34,197,94,0.18), rgba(34,197,94,0.06))',
    walking: 'linear-gradient(135deg, rgba(148,163,184,0.15), rgba(148,163,184,0.05))',
    private_car: 'linear-gradient(135deg, rgba(59,130,246,0.22), rgba(30,64,175,0.08))',
  };
  const accentColor = {
    yango: '#FBBF24',
    sotra: '#86EFAC',
    walking: '#CBD5E1',
    private_car: '#93C5FD',
  };

  // v2.19.7 — Couleurs CTA via styles inline imbattables (le CSS était battu
  // par d'autres règles inline en mode clair). On définit un mapping figé.
  const PROVIDER_BG = {
    yango:       '#F59E0B',
    sotra:       '#16A34A',
    walking:     '#475569',
    private_car: '#1E40AF',
  };
  const PROVIDER_FG = {
    yango:       '#0F172A',
    sotra:       '#FFFFFF',
    walking:     '#FFFFFF',
    private_car: '#FFFFFF',
  };

  return (
    <div data-testid="agent-transport-buttons" style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>🛣 Comment y aller</span>
        {origin?.label && <span style={{ fontStyle: 'italic', fontSize: 10 }}>depuis {origin.label}</span>}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 8,
        }}
      >
        {opts.map((o) => {
          const bg = cardBg[o.provider] || 'rgba(255,255,255,0.05)';
          const accent = accentColor[o.provider] || '#94A3B8';
          const priceLine = o.price_display || (o.price_fcfa ? `${o.price_fcfa} FCFA` : null);
          return (
            <div
              key={o.provider}
              data-testid={`agent-transport-${o.provider}`}
              style={{
                padding: 10,
                background: bg,
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ color: '#F1F5F9', fontSize: 13 }}>
                  {o.emoji ? `${o.emoji} ` : ''}{o.label}
                </strong>
                {Number.isFinite(o.eta_minutes) && (
                  <span style={{ fontSize: 11, color: accent, fontWeight: 700 }}>{o.eta_minutes} min</span>
                )}
              </div>
              {Number.isFinite(o.distance_km) && (
                <span style={{ fontSize: 11, color: '#94A3B8' }}>{o.distance_km} km</span>
              )}
              {priceLine && (
                <span style={{ fontSize: 12, color: accent, fontWeight: 600 }}>{priceLine}</span>
              )}
              {o.line_hint && (
                <span style={{ fontSize: 10, color: '#94A3B8', fontStyle: 'italic' }}>{o.line_hint}</span>
              )}
              <button
                type="button"
                className={`cta-transport cta-transport-${o.provider}`}
                data-testid={`agent-transport-${o.provider}-cta`}
                onClick={() => onClick(o)}
                disabled={o.provider === 'walking'}
                style={{
                  marginTop: 4,
                  padding: '6px 10px',
                  background: PROVIDER_BG[o.provider] || 'rgba(255,255,255,0.06)',
                  backgroundImage: 'none',
                  color: PROVIDER_FG[o.provider] || '#FFFFFF',
                  border: 'none',
                  borderRadius: 6,
                  cursor: o.provider === 'walking' ? 'default' : 'pointer',
                  fontSize: 11,
                  fontWeight: 700,
                  opacity: o.provider === 'walking' ? 0.7 : 1,
                }}
              >
                {o.cta || o.label}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RouteSummary({ component }) {
  return (
    <div data-testid="agent-route-summary" style={{
      marginTop: 6, padding: "6px 10px", fontSize: 12, color: "#CBD5E1",
      background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 6,
    }}>
      🛣 {component.summary}
    </div>
  );
}

function AddressPreview({ component }) {
  return (
    <div data-testid="agent-address-preview" style={{
      marginTop: 8, padding: "10px 12px", background: "rgba(59,130,246,0.1)",
      border: "1px solid rgba(59,130,246,0.4)", borderRadius: 8, fontSize: 13,
    }}>
      <strong style={{ color: "#93C5FD" }}>📍 Adresse CIV générée (preview)</strong>
      <div><code style={{ fontSize: 12, color: "#F1F5F9" }}>{component.address_civ}</code></div>
      {component.adresse_textuelle && <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>{component.adresse_textuelle}</div>}
    </div>
  );
}

export default function AgentToolBlock({ components }) {
  const userId = useUserId();
  if (!Array.isArray(components) || components.length === 0) return null;
  return (
    <div data-testid="agent-tool-block" style={{ marginTop: 8 }}>
      {components.map((c, i) => {
        const key = `${c.type}-${i}`;
        if (c.type === "place_list") return <PlaceList key={key} items={c.items} query={c.query} />;
        if (c.type === "map") return <MapBlock key={key} component={c} userId={userId} />;
        if (c.type === "transport_buttons") return <TransportButtons key={key} component={c} />;
        if (c.type === "route_summary") return <RouteSummary key={key} component={c} />;
        if (c.type === "address_preview") return <AddressPreview key={key} component={c} />;
        if (c.type === "ordonnance_card") return <OrdonnanceCard key={key} data={c.data} />;
        if (c.type === "cni_card") return <CniCard key={key} data={c.data} />;
        if (c.type === "facture_card") return <FactureCard key={key} data={c.data} />;
        if (c.type === "analyse_card") return <AnalyseMedicaleCard key={key} data={c.data} />;
        if (c.type === "country_note") return <CountryNote key={key} text={c.text} />;
        return null;
      })}
    </div>
  );
}
